import React, { useEffect, useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import {
  Card,
  CardContent,
  CardActions,
  IconButton,
  Grid,
  Tooltip,
  Typography,
  Button,
  Paper,
  Collapse,
  CircularProgress,
} from '@material-ui/core';
import AddCircleIcon from '@material-ui/icons/AddCircle';
import DeleteIcon from '@material-ui/icons/Delete';
import SaveIcon from '@material-ui/icons/Save';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import FormInput from '../ReactHookForm/FormInput/FormInput';
import * as clientService from '../../services/client.service';
import { useFormContext } from 'react-hook-form';



const useStyles = makeStyles((theme) => ({
  root: {
    width: '100%',
    marginBottom: theme.spacing(1),
  },
  content: {
    paddingBottom: theme.spacing(1),
  },
  field: {
    marginBottom: theme.spacing(2),
  },
  actions: {
    display: 'flex',
    justifyContent: 'flex-end',
    paddingTop: 0,
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
  },
  headerContent: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
  },
  summaryText: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    color: theme.palette.text.secondary,
  },
  section: {
    backgroundColor: theme.palette.grey[50],
    border: `1px solid ${theme.palette.grey[200]}`,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(1),
    height: 380,
  },
  scrollContainer: {
    height: 320,
    overflowY: 'auto',
    overflowX: 'hidden',
    padding: theme.spacing(1),
    backgroundColor: theme.palette.common.white,
    border: `1px solid ${theme.palette.grey[200]}`,
    borderRadius: theme.shape.borderRadius,
    '& .MuiGrid-container': {
      margin: 0,
      width: '100%',
    },
    '& .select-input-container': {
      overflow: 'visible',
    },
  },
  productScrollContainer: {
    height: 200,
    overflowY: 'auto',
    overflowX: 'hidden',
    padding: theme.spacing(1),
    backgroundColor: theme.palette.common.white,
    border: `1px solid ${theme.palette.grey[200]}`,
    borderRadius: theme.shape.borderRadius,
    '& .MuiGrid-container': {
      margin: 0,
      width: '100%',
    },
    '& .select-input-container': {
      overflow: 'visible',
    },
  },
  sectionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(1),
  },
  defaultProduct: {
    marginBottom: theme.spacing(1),
    backgroundColor: theme.palette.common.white,
    border: `1px solid ${theme.palette.grey[200]}`,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(1),
    height: 90,
  },
  defaultProductCaption: {
    display: 'block',
    marginBottom: theme.spacing(0.5),
  },
  archiveFolderTooltip: {
    fontSize: 15,
  },

}));

const FORM_FIELDS = {
  NAME: 'name',
  DESCRIPTION: 'description',
  AUSUM_SWEEP_TYPE_ID: 'ausumSweepTypeId',
  ACTIVE: 'active',
  PDF_RETURN: 'pdfReturn',
  HOST: 'host',
  PORT: 'port',
  PROTOCOL: 'protocol',
  USERNAME: 'username',
  PASSWORD: 'password',
  FOLDER_LOCATION: 'folderLocation',
  RETURN_FOLDER: 'returnFolder',
  ARCHIVE_FOLDER: 'archiveFolder',
  BRANCH_NUMBER: 'branchNumber',
  CLIENT_ID: 'clientId',
  SURVEY_METHOD: 'surveyMethod',
  PRODUCT_ID: 'productId',
  SWEEP_FREQUENCY: 'sweepFrequency',
  REFERENCE_NO: 'referenceNo',
};

const buildPath = (...paths) => paths.join('.');

export const AusumConfigForm = ({
  config,
  sweepTypes,
  onSave,
  onDelete,
  clients,
  products,
}) => {
  const [isSaving, setIsSaving] = useState(false);
  const classes = useStyles();
  const { handleSubmit, reset, watch, setValue } = useFormContext();
  const selectedProtocol = watch(buildPath('ausumFtpconfig', '0', FORM_FIELDS.PROTOCOL));
  const [expanded, setExpanded] = useState(config.ausumSweepConfigId === 0);
  
  // Scroll into view when a new configuration is added
  useEffect(() => {
    if (config.ausumSweepConfigId === 0) {
      const element = document.getElementById(`config-${config.ausumSweepConfigId}`);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  }, [config.ausumSweepConfigId]);

  const name = watch(FORM_FIELDS.NAME);
  const description = watch(FORM_FIELDS.DESCRIPTION);
  const active = watch(FORM_FIELDS.ACTIVE);
  const referenceNo = watch(FORM_FIELDS.REFERENCE_NO);

  useEffect(() => {
    // No need to reset form values here since they're initialized in ConfigurationFormWrapper
  }, []);


  const onSubmit = async (data) => {
    setIsSaving(true);
    try {
      await onSave({
      ausumSweepConfigId: data.ausumSweepConfigId,
      ausumSweepTypeId: data.ausumSweepTypeId,
      name: data.name,
      description: data.description,
      referenceNo: data.referenceNo,
      createdOn: data.createdOn,
      createdBy: data.createdBy,
      modifiedOn: data.modifiedOn,
      modifiedBy: data.modifiedBy,
      active: data.active,
      pdfReturn: data.pdfReturn,
      sweepFrequency: data.sweepFrequency,
      ausumSweepType: data.ausumSweepType,
      ausumClientLookup: data.ausumClientLookup || [],
      ausumFtpconfig: data.ausumFtpconfig || [],
      ausumProductLookup: data.ausumProductLookup || [],
    });
    } catch (error) {
      throw error;
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Card className={classes.root} id={`config-${config.ausumSweepConfigId}`}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <CardContent className={classes.content}>
          <div 
            className={classes.header} 
            onClick={() => setExpanded(!expanded)}
          >
            <div className={classes.headerContent}>
              {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              <Typography variant="h6">
                {name || 'New Configuration'}
              </Typography>
              
            </div>
           
          </div>

          <Collapse in={expanded}>
              <Grid container spacing={1} style={{ marginTop: '16px' }}>
              <Grid item xs={2}>
                <FormInput
                  type="text"
                  name={FORM_FIELDS.NAME}
                  label="Name"
                />
              </Grid>
              <Grid item xs={4}>
                <FormInput
                  type="text"
                  name={FORM_FIELDS.DESCRIPTION}
                  label="Description"
                />
              </Grid>
              <Grid item xs={1}>
                <FormInput
                  type="text"
                  name={FORM_FIELDS.REFERENCE_NO}
                  label="Reference Number"
                  defaultValue={config.referenceNo}
                />
              </Grid>
              <Grid item xs={1}>
                <FormInput
                  type="select"
                  name={FORM_FIELDS.SWEEP_FREQUENCY}
                  label="Sweeps/Day"
                  defaultValue={config.sweepFrequency}
                  required
                  rules={{ required: 'Sweeps per day is required' }}
                  options={[
                    { value: 1, label: '1x' },
                    { value: 2, label: '2x' },
                    { value: 3, label: '3x' },
                    { value: 4, label: '4x' },
                    { value: 5, label: '5x' },
                    { value: 6, label: '6x' },
                  ]}
                />
              </Grid>
              <Grid item xs={1}>
                <FormInput
                  type="select"
                  name={FORM_FIELDS.AUSUM_SWEEP_TYPE_ID}
                  label="Type"
                  options={sweepTypes.map(type => ({
                    value: type.ausumSweepTypeId,
                    label: type.name,
                  }))}
                />
              </Grid>
              <Grid item xs={2} style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '16px' }}>
                <FormInput
                  className={classes.formInput}
                  type="switch"
                  name={FORM_FIELDS.PDF_RETURN}
                  label="PDF Return"
                />
                <FormInput
                  className={classes.formInput}
                  type="switch"
                  name={FORM_FIELDS.ACTIVE}
                  label="Active"
                />
              </Grid>
            </Grid>
          {watch(FORM_FIELDS.AUSUM_SWEEP_TYPE_ID) === 2 && (
            <Grid container spacing={1} style={{ marginTop: '8px' }}>
              <Grid item xs={2}>
                <FormInput
                  type="select"
                  name={buildPath('ausumFtpconfig', '0', FORM_FIELDS.PROTOCOL)}
                  label="Protocol"
                  options={[
                    { value: 'ftp', label: 'FTP' },
                    { value: 'sftp', label: 'SFTP' },
                    { value: 'ftps', label: 'FTPS' },
                  ]}
                />
              </Grid>
              <Grid item xs={3}>
                <FormInput
                  type="text"
                  name={buildPath('ausumFtpconfig', '0', FORM_FIELDS.HOST)}
                  label="Host"
                />
              </Grid>
              <Grid item xs={1}>
                <Tooltip 
                  title={
                    <>
                      Default ports:<br />
                      FTP: 21<br />
                      SFTP: 22<br />
                      FTPS: 990
                      <br /><br />
                      Currently selected would typically be: {
                        selectedProtocol === 'ftp' ? '21' :
                        selectedProtocol === 'sftp' ? '22' :
                        selectedProtocol === 'ftps' ? '990' : 
                        'Select a protocol'
                      }
                    </>
                  }
                  placement="top"
                >
                  <div>
                    <FormInput
                      type="number"
                      name={buildPath('ausumFtpconfig', '0', FORM_FIELDS.PORT)}
                      label="Port"
                    />
                  </div>
                </Tooltip>
              </Grid>
              <Grid item xs={2}>
                <FormInput
                  type="text"
                  name={buildPath('ausumFtpconfig', '0', FORM_FIELDS.USERNAME)}
                  label="Username"
                />
              </Grid>
              <Grid item xs={2}>
                <FormInput
                  type="text"
                  name={buildPath('ausumFtpconfig', '0', FORM_FIELDS.PASSWORD)}
                  label="Password"
                />
              </Grid>
              <Grid item xs={3}>
                <FormInput
                  type="text"
                  name={buildPath('ausumFtpconfig', '0', FORM_FIELDS.FOLDER_LOCATION)}
                  label="Folder Location"
                />
              </Grid>
              <Grid item xs={3}>
                <FormInput
                  type="text"
                  name={buildPath('ausumFtpconfig', '0', FORM_FIELDS.RETURN_FOLDER)}
                  label="Return Folder"
                />
              </Grid>
              <Grid item xs={3}>
                <div style={{ display: 'flex', alignItems: 'flex-start' }}>
                  <FormInput
                    type="text"
                    name={buildPath('ausumFtpconfig', '0', FORM_FIELDS.ARCHIVE_FOLDER)}
                    label="Archive Folder"
                  />
                  <Tooltip
                    title="Leaving this field blank will delete files after import rather than archiving them."
                    placement="top"
                    classes={{ tooltip: classes.archiveFolderTooltip }}
                  >
                    <span>
                      <IconButton size="small" style={{ padding: 4, marginTop: 20 }} disabled>
                        <InfoOutlinedIcon fontSize="small" color="action" />
                      </IconButton>
                    </span>
                  </Tooltip>
                </div>
              </Grid>
            </Grid>
          )}

          <Grid container spacing={1} style={{ marginTop: '8px' }}>
            <Grid item xs={6}>
              <div className={classes.section}>
                <div className={classes.sectionHeader}>
                  <Typography variant="subtitle1">Client Mappings</Typography>
                  <IconButton
                    size="small"
                    onClick={() => {
                      const currentLookups = watch('ausumClientLookup') || [];
                      setValue('ausumClientLookup', [
                        ...currentLookups,
                        {
                          branchNumber: '',
                          clientId: null,
                          createdBy: config.createdBy,
                          modifiedBy: config.modifiedBy
                        }
                      ]);
                    }}
                    style={{ color: '#4caf50' }}
                  >
                    <AddCircleIcon />
                  </IconButton>
                </div>
                <Paper className={classes.scrollContainer}>
                {(watch('ausumClientLookup') || []).map((lookup, index) => (
                  <Grid container spacing={1} key={lookup.ausumClientLookupId || index} >
                    <Grid item xs={4}>
                      <FormInput
                        type="text"
                        name={`ausumClientLookup.${index}.branchNumber`}
                        label="BranchOffCd"
                        isAbsolute={true}
                      />
                    </Grid>
                    <Grid item xs={7} className="select-input-container">
                      <FormInput
                        type="select"
                        name={`ausumClientLookup.${index}.clientId`}
                        label="Client"
                        options={clients.map(client => ( {
                          value: client.id,
                          label: client.name + ' (' + client.code + ')'
                        }))}
                        isAbsolute={true}
                      />
                    </Grid>
                    <Grid item xs={1} style={{ display: 'flex', alignItems: 'flex-start', paddingLeft: 0, paddingTop: '32px' }}>
                      <IconButton
                        size="small"
                        onClick={() => {
                          const currentLookups = watch('ausumClientLookup') || [];
                          setValue('ausumClientLookup', currentLookups.filter((_, i) => i !== index));
                        }}
                      >
                        <DeleteIcon style={{ color: '#f44336' }}/>
                      </IconButton>
                    </Grid>
                  </Grid>
                ))}
                </Paper>
              </div>
            </Grid>

            <Grid item xs={6}>
              <div className={classes.section}>
                <div className={classes.sectionHeader}>
                  <Typography variant="subtitle1">Product Mappings</Typography>
                  <IconButton
                    size="small"
                    onClick={() => {
                      const currentLookups = watch('ausumProductLookup') || [];
                      setValue('ausumProductLookup', [
                        ...currentLookups,
                        {
                          surveyMethod: '',
                          productId: null,
                          createdBy: config.createdBy,
                          modifiedBy: config.modifiedBy
                        }
                      ]);
                    }}
                    style={{ color: '#4caf50' }}
                  >
                    <AddCircleIcon />
                  </IconButton>
                </div>

                <div className={classes.defaultProduct}>
                  <Typography variant="caption" className={classes.defaultProductCaption}>
                    Default Product (used when no survey method matches)
                  </Typography>
                  <FormInput
                    type="select"
                    name="ausumProductLookup.0.productId"
                    label="Default Product"
                    options={products.map(product => ({
                      value: product.productId,
                      label: `${product.name} (${product.productCode})`
                    }))}
                    defaultValue={products.find(product => product.productId === (watch('ausumProductLookup.0') || {}).productId)}
                  />
                </div>

                <Paper className={classes.productScrollContainer}>
                {(watch('ausumProductLookup') || []).slice(1).map((lookup, index) => {
                  // Add 1 to index since we sliced off the first item
                  const actualIndex = index + 1;
                  return (
                    <Grid container spacing={1} key={lookup.ausumProductLookupId || actualIndex}>
                      <Grid item xs={4}>
                        <FormInput
                          type="text"
                          name={`ausumProductLookup.${actualIndex}.surveyMethod`}
                          label="Survey Method/LOB"
                          placeholder="Leave empty for default"
                        />
                      </Grid>
                      <Grid item xs={7} className="select-input-container">
                        <FormInput
                          type="select"
                          name={`ausumProductLookup.${actualIndex}.productId`}
                          label="Product"
                          options={products.map(product => ({
                            value: product.productId,
                            label: `${product.name} (${product.productCode})`
                          }))}
                          defaultValue={products.find(product => product.productId === lookup.productId)}
                          isAbsolute={true}
                        />
                      </Grid>
                      <Grid item xs={1} style={{ display: 'flex', alignItems: 'flex-start', paddingLeft: 0, paddingTop: '32px' }}>
                        <IconButton
                          size="small"
                          onClick={() => {
                            const currentLookups = watch('ausumProductLookup') || [];
                            setValue('ausumProductLookup', currentLookups.filter((_, i) => i !== actualIndex));
                          }}
                        >
                          <DeleteIcon style={{ color: '#f44336' }}/>
                        </IconButton>
                      </Grid>
                    </Grid>
                  );
                })}
                </Paper>
              </div>
            </Grid>
          </Grid>
          </Collapse>
        </CardContent>
        {expanded && (
          <CardActions className={classes.actions}>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              startIcon={isSaving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
              style={{ marginLeft: 'auto', minWidth: '100px' }}
              disabled={isSaving}
            >
              {isSaving ? 'Saving...' : 'Save'}
            </Button>
          </CardActions>
        )}
      </form>
    </Card>
  );
};
